import {CSSProperties, SyntheticEvent, useState} from 'react'
import {logger} from 'tizra'
import * as B from '../block'
import {coverPos, flexHorizontal} from '../components/Background/utils'
import {ImageBlockConfig} from './meta'
import S from './styles.module.css'

const log = logger('ImageBlock/Complex')

const maxDim = (dim?: string) =>
  !dim ? 'calc(infinity * 1px)'
  : parseInt(dim).toString() === dim ? `${dim}px`
  : dim

interface ComplexProps {
  imageUrl: string
  altText: string
  setErrored: (url: string) => void
  linkUrl: string
  maxWidth: string
  maxHeight: string
  fitting: ImageBlockConfig['fitting']
  position: ImageBlockConfig['position']
  focus: ImageBlockConfig['focus']
  surround: ImageBlockConfig['surround']
}

export const Complex = ({
  imageUrl,
  altText,
  setErrored,
  linkUrl,
  maxWidth,
  maxHeight,
  fitting,
  position,
  focus,
  surround,
}: ComplexProps) => {
  const [{naturalWidth, naturalHeight}, setDims] = useState<{
    naturalWidth?: number
    naturalHeight?: number
  }>({})

  const haveDimensions = !!(naturalWidth && naturalHeight)

  const onLoad = (e: SyntheticEvent<HTMLImageElement>) => {
    const {
      currentTarget: {naturalWidth, naturalHeight},
    } = e
    // TODO: SVG doesn't have natural dimensions
    if (naturalWidth && naturalHeight) {
      setDims({naturalWidth, naturalHeight})
    } else {
      log.warn('Image loaded with zero natural dimensions:', {
        imageUrl,
        naturalWidth,
        naturalHeight,
      })
      setErrored(imageUrl)
    }
  }

  const onError = (_e: SyntheticEvent<HTMLImageElement>) => setErrored(imageUrl)

  const vars: CSSProperties | null =
    !haveDimensions ? null : (
      {
        '--max-width': maxDim(maxWidth),
        '--max-height': maxDim(maxHeight),
        '--natural-width': `${naturalWidth}px`,
        '--natural-height': `${naturalHeight}px`,
        '--aspect-ratio': naturalWidth / naturalHeight,
      }
    )

  const fitStyle: CSSProperties =
    !vars ? {}
    : fitting === 'cover' ?
      {
        ...vars,
        '--width-limit': 'var(--max-width)',
        '--height-limit': 'var(--max-height)',
        width: 'min(100%, var(--width-limit))',
        height: 'auto',
        maxHeight: 'var(--height-limit)',
        objectFit: 'cover',
        objectPosition: coverPos[focus],
      }
    : fitting === 'contain' ?
      {
        ...vars,
        '--width-limit': 'var(--max-width)',
        '--height-limit': 'var(--max-height)',
        width:
          'min(100%, var(--width-limit), calc(var(--height-limit) * var(--aspect-ratio)))',
        height: 'auto',
      }
    : fitting === 'scale-down' ?
      {
        ...vars,
        '--width-limit': 'min(var(--max-width), var(--natural-width))',
        '--height-limit': 'min(var(--max-height), var(--natural-height))',
        width:
          'min(100%, var(--width-limit), calc(var(--height-limit) * var(--aspect-ratio)))',
        height: 'auto',
      }
    : (log.error('unreachable case:', {fitting}) as never)

  const image = (
    <B.Image
      src={imageUrl}
      alt={altText}
      style={fitStyle}
      onLoad={onLoad}
      onError={onError}
      shadowed={surround === 'shadow'}
      {...B.tid('ImageBlock-img')}
    />
  )

  return (
    <div
      className={haveDimensions ? S.wrapper : S.hidden}
      style={{justifyContent: flexHorizontal[position]}}
      {...B.tid('ImageBlock-wrapper')}
    >
      {linkUrl ?
        <B.UniversalLink
          href={linkUrl}
          style={fitStyle}
          {...B.tid('ImageBlock-link')}
        >
          {image}
        </B.UniversalLink>
      : image}
    </div>
  )
}
